import { CHAT_HISTORY_BOTTOM_ANCHOR_ID } from "./const";

let isAutoScrollEnabled = true;
let scrollableNode: HTMLElement | null = null;
let removeScrollListener: (() => void) | null = null;
let intersectionObserver: IntersectionObserver | null = null;

const AUTO_SCROLL_BOTTOM_THRESHOLD = 120;

export function setIsAutoScrollEnabled(value: boolean) {
  if (!value) {
    setTimeout(() => {
      isAutoScrollEnabled = value;
    }, 200);
    return;
  }
  isAutoScrollEnabled = value;
}

const detachScrollListener = () => {
  if (removeScrollListener) {
    try {
      removeScrollListener();
    } finally {
      removeScrollListener = null;
    }
  }
};

/**
 * 使用滚动位置判断用户是否离开底部，避免因内容增高误判。
 * 仅当用户真的滚离底部时关闭 autoScroll，滚回底部或被动滚动时重新开启。
 */
export function registerAutoScrollContainer(node: HTMLElement | null) {
  if (node === scrollableNode) {
    return;
  }

  detachScrollListener();
  scrollableNode = node;

  if (!node) {
    return;
  }

  const handleScroll = () => {
    const distanceFromBottom =
      node.scrollHeight - node.scrollTop - node.clientHeight;
    const shouldStickToBottom =
      distanceFromBottom <= AUTO_SCROLL_BOTTOM_THRESHOLD;

    setIsAutoScrollEnabled(shouldStickToBottom);
  };

  node.addEventListener("scroll", handleScroll, { passive: true });
  removeScrollListener = () => {
    node.removeEventListener("scroll", handleScroll);
  };

  // 初始化时同步一次状态
  handleScroll();
}

export function scrollToBottom(force = false) {
  if (!isAutoScrollEnabled && !force) {
    return;
  }
  const anchorElement = document.getElementById(CHAT_HISTORY_BOTTOM_ANCHOR_ID);
  if (anchorElement) {
    anchorElement.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }
}

/**
 * 初始化基于 IntersectionObserver 的 autoScroll 开关逻辑：
 * - 当底部锚点进入视野：开启 autoScroll
 * - 关闭 autoScroll 的逻辑由滚动事件负责，避免内容增高时误判
 *
 * root 自动探测为 SimpleBar 的滚动容器（.simplebar-content-wrapper），若未探测到则退化为 viewport。
 */
export function initAutoScrollObserver() {
  if (typeof window === "undefined") {
    return;
  }

  const anchorElement = document.getElementById(CHAT_HISTORY_BOTTOM_ANCHOR_ID);
  if (!anchorElement) {
    return;
  }

  // 优先使用 SimpleBar 的滚动容器作为 root
  const simpleBarViewport =
    scrollableNode ||
    (anchorElement.closest(".simplebar-content-wrapper") as Element | null);

  // 清理已有的 observer，避免重复绑定
  if (intersectionObserver) {
    try {
      intersectionObserver.disconnect();
    } catch {}
  }

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;

      // 只在回到底部时打开 autoScroll，避免内容高度突增时误关闭
      if (entry.isIntersecting) {
        setIsAutoScrollEnabled(true);
      }
    },
    {
      root: simpleBarViewport || null,
      threshold: 0,
      rootMargin: `0px 0px 60px 0px`,
    }
  );

  intersectionObserver.observe(anchorElement);
}

export function destroyAutoScrollObserver() {
  if (intersectionObserver) {
    try {
      intersectionObserver.disconnect();
    } finally {
      intersectionObserver = null;
    }
  }

  detachScrollListener();
  scrollableNode = null;
}
